/* severanceValidate -- THE ONE CLIENT-SIDE SEVERANCE VALIDATOR. Two surfaces, one rule.
 *
 * These checks were written for the Payroll TermModal (PayrollPage, step 4) and lived inline
 * there. ScenariosPage had NONE AT ALL: `setSevField` wrote the raw string, `severanceFor`
 * does not clamp, the `min`/`max` attributes are DECORATIVE (there is no <form> and no
 * checkValidity() anywhere in either page), and the scenarios PUT stores an unvalidated blob
 * -- so a negative or a 60-week severance was accepted, modelled and saved.
 *
 * ⇒ THE RULES ARE EXTRACTED HERE RATHER THAN COPIED. A second implementation of one rule is
 * the shape this project has paid for repeatedly (PRINCIPLE 0), and a picker and a guard that
 * disagree about what is legal IS the defect (the 09-15 implementation-target lesson).
 *
 * THE RULES, mirrored from the server's own validator (server.js validateSeverance):
 *   no mode                 -> no severance. VALID. (This is how "none" is expressed.)
 *   a mode with NO value    -> INCOMPLETE, not zero. Refused, naming which number is wanted.
 *   a non-number            -> refused.
 *   a negative              -> refused.
 *   weeks > 52              -> refused. The server enforces the same bound.
 *   a deliberate 0          -> ** ACCEPTED **. A typed 0 is an ANSWER, not an absence, and
 *                              the whole three-state design rests on it surviving. Never
 *                              `|| 0` and never a silent clamp (rule 44 / section E1).
 *   COBRA                   -> when set: must be a number and non-negative. An UNSET cobra
 *                              (empty string, null or undefined) is simply absent.
 *
 * ⛔ NOTHING HERE CLAMPS. A 60 is REFUSED WITH A MESSAGE; it never becomes a 52. Silently
 * correcting an operator's answer is the section E1 shape, and it is what the TermModal
 * already refused to do.
 *
 * THE MESSAGE STRINGS ARE THE TermModal's OWN, character for character, so that surface's
 * behaviour is unchanged by the extraction.
 */

/* severanceError({mode, value, cobra}) -> a message string, or null when valid.
   `value` and `cobra` are accepted as STRINGS (which is how both surfaces hold them: only a
   string can tell '' from '0'), but a number works too. */
export function severanceError(sev) {
    const s = sev || {};
    const mode = s.mode;
    if (!mode) return null;
    const value = s.value;
    if (value === "" || value === null || value === undefined) {
        return mode === 'weeks' ? 'Enter the number of weeks (or 0).' : 'Enter the amount (or 0).';
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return 'That is not a number.';
    if (n < 0) return 'Severance cannot be negative.';
    if (mode === 'weeks' && n > 52) return 'Maximum 52 weeks.';
    const cobra = s.cobra;
    if (cobra !== "" && cobra !== null && cobra !== undefined) {
        const c = Number(cobra);
        if (!Number.isFinite(c)) return 'COBRA is not a number.';
        if (c < 0) return 'COBRA cannot be negative.';
    }
    return null;
}

/* severanceErrorForTermination(t) -> the same, for a ScenariosPage termination RECORD.
 *
 * It reads the record EXACTLY as runScenario and the row display read it -- including the
 * legacy `severanceWeeks` key, which every one of the six stored scenarios carries. That
 * shared reading is the point: a validator that understood the record differently from the
 * engine would refuse things the model accepts, or pass things it cannot use.
 *
 * ⓘ A legacy record holding severanceWeeks 0 yields NO mode, so it is VALID and models as no
 * severance -- which is what those six rows have always meant. */
export function severanceErrorForTermination(t) {
    const s = t || {};
    const mode = s.severanceMode || (Number(s.severanceWeeks) > 0 ? 'weeks' : '');
    if (!mode) return null;
    const raw = s.severanceMode ? s.severanceValue : s.severanceWeeks;
    const value = (raw === undefined || raw === null) ? '' : String(raw);
    const cobra = (s.severanceCobra === undefined || s.severanceCobra === null) ? '' : String(s.severanceCobra);
    return severanceError({ mode, value, cobra });
}

/* Batch 1b (2026-11-03, L3648): terminationRecordError(t) -> the PER-FIELD check of one scenario termination record, or null.
 * BOTH STORED SHAPES are valid: the legacy name-keyed { name, termDate, severanceWeeks } (every stored scenario carries it) and the
 * current { id, name, termDate, severanceWeeks, severanceMode, severanceValue, severanceCobra }. Field by field: WHO (an id or a
 * name -- the name alone is the legacy key), WHICH MONTH (termDate 'YYYY-MM'), and the severance (severanceErrorForTermination, the
 * reading runScenario uses). The window is NOT checked: a stored month the forecast has since passed stays a legal record. */
export function terminationRecordError(t) {
    if (!t || typeof t !== 'object') return 'A termination is not a record.';
    const who = (t.id !== undefined && t.id !== null && t.id !== '') ? null : (typeof t.name === 'string' && t.name.trim() ? null : 'A termination names no employee.');
    if (who) return who;
    const label = typeof t.name === 'string' && t.name.trim() ? t.name.trim() : 'employee ' + t.id;
    if (typeof t.termDate !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(t.termDate)) return 'The termination of ' + label + ' has no month.';
    const sev = severanceErrorForTermination(t);
    return sev ? 'The termination of ' + label + ': ' + sev : null;
}
